import { Accessor, JSXElement, ParentProps, Signal, createEffect, createSignal, onCleanup, createContext, useContext } from "solid-js";
import { render } from "solid-js/web";

import styles from './contextmenu.module.css';

// Prepare the tooltip directive
declare module "solid-js" {
    namespace JSX {
        interface Directives {
            contextMenu: JSXElement;
        }
    }
}

declare global {
    interface Window {
        contextMenu: (el: HTMLElement, params: Accessor<JSXElement>) => void;
    }
}

class ContextMenuContext {
    parent: HTMLElement

    constructor(parent: HTMLElement) {
        this.parent = parent;
    }

    close() {
        closeContextMenu();
    }
}

const ContextMenuContextInstance = createContext(new ContextMenuContext(undefined!));
export function useContextMenuContext() { return useContext(ContextMenuContextInstance) }

window.contextMenu = window.contextMenu ?? ((el: HTMLElement, params: Accessor<JSXElement>) => {
    el.addEventListener('contextmenu', ev => {
        if (ev.ctrlKey) return;

        const ctx = new ContextMenuContext(el);
        ev.preventDefault();

        closeInstance = render(() => <ContextMenuContextInstance.Provider value={ctx}>
            <ContextMenu x={ev.pageX} y={ev.pageY}>
                {params()}
            </ContextMenu>
        </ContextMenuContextInstance.Provider>, contextMenuContainer);
    });
});

let closeInstance: (() => void) | undefined;

document.addEventListener('mousedown', closeContextMenu);
window.addEventListener('focusout', closeContextMenu);
window.addEventListener('blur', closeContextMenu);
function closeContextMenu() {
    if (closeInstance) {
        closeInstance();
        closeInstance = undefined;
    }
}

let contextMenuContainer = document.createElement('div');
document.addEventListener('DOMContentLoaded', () => {
    contextMenuContainer.classList.add(styles.contextMenuContainer);
    document.body.appendChild(contextMenuContainer);
});

interface ContextMenuOptions {
    x: number,
    y: number
}

function ContextMenu(props: ParentProps<ContextMenuOptions>) {
    const ctx = useContextMenuContext();

    function initElement(el: HTMLDivElement) {
        el.addEventListener('mousedown', ev => ev.stopPropagation());

        reposition(el, props.x, props.y);
    }

    return <div ref={initElement} class={`${styles.contextMenu} contextMenu flexbox vertical`}>
        {props.children}
    </div>
}

function reposition(el: HTMLElement, x: number, y: number) {
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;

    const bodyRect = document.body.getBoundingClientRect();
    const elRect = el.getBoundingClientRect();

    if (elRect.bottom < bodyRect.bottom && elRect.top - elRect.height > 0) {
        el.style.top = `${x - elRect.height}px`;
    }
    if (elRect.right < bodyRect.right && elRect.left - elRect.width > 0) {
        el.style.left = `${y - elRect.width}px`;
    }
}
